'use client'

import { siteConfig } from '@/config/site.config'
import { Address, Partner } from '@/types/partner'
import { GoogleMap, InfoWindow, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api'
import { useEffect, useRef, useState } from 'react'
import { LangCode } from '../i18n/lang-context'
import { PartnerCard } from './partner-card'

const containerStyle = { width: '100%', height: '420px' }

const mapStyle = [
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'all',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#1f2937' }],
  },
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#e5e7eb' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#dbeafe' }],
  },
]
const centerFrance = { lat: 46.603354, lng: 1.888334 }
interface Selection {id: string, partnerId: string, address: Address}

export default function PartnersMap({
  langCode, shown, mapPartners, partnerId, partnerOnly, countryCode, area, city}: {
  langCode: LangCode
  shown: boolean
  mapPartners: {[partnerId: string]: Partner}
  partnerId: string|undefined
  partnerOnly: boolean
  countryCode: string|undefined
  area: string|undefined
  city: string|undefined
}) {

  const [defaultMarkerIcon, setDefaultMarkerIcon] = useState<google.maps.Symbol>({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
    fillColor: '#111827', // gris très foncé (premium)
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: 1.6,
  })
  const [partnerMarkerIcon, setPartnerMarkerIcon] = useState<google.maps.Symbol>({
    ...defaultMarkerIcon,
    fillColor: '#009bdf', // bleu legibloq clair
    scale: 1.75,
  })
  const [activeMarkerIcon, setActiveMarkerIcon] = useState<google.maps.Symbol>({
    ...defaultMarkerIcon,
    fillColor: '#005baa',// bleu legibloq foncé
    scale: 1.9,
  })
  
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: siteConfig.google.mapApiKey!,
    language: 'fr'
  })

  const [visiblePartners, setVisiblePartners] = useState<Selection[]>([])
  const [selected, setSelected] = useState<Selection>()
  const mapRef = useRef<google.maps.Map | null>(null)

  useEffect(() => {
    if(!shown) return 
    const _visiblePartners: Selection[] = []
    Object.values(mapPartners).map((partner) => {
      if(partnerOnly && partner.id !== partnerId) return
      for(let j = 0; j < partner.addresses.length; j++) {
        const address = partner.addresses[j]
        if((!countryCode || countryCode === address.countryCode) &&
           (!area || area === address.area) &&
           (!city || city === address.city)) {
          _visiblePartners.push({id: partner.id + address.latitude + address.longitude, partnerId: partner.id, address: address})
        }
      }
    })
    setVisiblePartners(_visiblePartners)
  }, [shown, mapPartners, partnerOnly, countryCode, area, city])

  const showPartners = () => {
    if (!mapRef.current || !visiblePartners.length) return
    if (visiblePartners.length === 1) {
      mapRef.current!.setCenter({
        lat: visiblePartners[0].address.latitude,
        lng: visiblePartners[0].address.longitude,
      })
      mapRef.current!.setZoom(14)
      openInfoWindow(visiblePartners[0])
      return
    }
    closeInfoWindow()
    const bounds = new google.maps.LatLngBounds()
    visiblePartners.forEach(partner => {
      bounds.extend({ lat: partner.address.latitude, lng: partner.address.longitude })
    })
    mapRef.current.fitBounds(bounds)
  }

  useEffect(() => {
    showPartners()
  }, [visiblePartners])

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .gm-style .gm-style-iw-ch { display: none !important; }
      .gm-style .gm-ui-hover-effect { display: none !important; }
      .gm-style .gm-style-iw-c { padding: 0 !important; border-radius: 10px !important; }
      .gm-style .gm-style-iw-d { overflow: hidden !important; }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const getSelectedPartnerForCard = () => {
    const partner = {...mapPartners[(selected as Selection).partnerId]}
    const address = (selected as Selection).address
    partner.location = {fr: address.addressLine + ' ' + address.zipCode + ' ' + address.city}
    if(address.website)
      partner.website = address.website
    return partner
  }
  const openInfoWindow = (partner: Selection) => {
    setSelected(partner)
  }
  const closeInfoWindow = () => {
    setSelected(undefined)
  }
  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setDefaultMarkerIcon({
      ...defaultMarkerIcon,
      anchor: new window.google.maps.Point(12, 24), // 🔥 clé ici (mauvais positionnement au dézomme)
      // Quand on dézoome : Google Maps “simplifie” le rendu
      // le marker est positionné par rapport à son point d’ancrage
      // si SVG n’est pas bien centré → il “dérive visuellement”
    })
    setPartnerMarkerIcon({
      ...partnerMarkerIcon,
      anchor: new window.google.maps.Point(12, 24)
    })
    setActiveMarkerIcon({
      ...activeMarkerIcon,
      anchor: new window.google.maps.Point(12, 24)
    })
    showPartners()
  }
  const onUnload = () => {
    mapRef.current = null
  }

  if (!isLoaded) return null

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={centerFrance}
      zoom={5}
      onLoad={(map) => onLoad(map)}
      onUnmount={() => onUnload()}
      onClick={() => closeInfoWindow()}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
        styles: mapStyle 
      }}
    >
      <MarkerClusterer>
        {(clusterer) => <>
          {visiblePartners.map(partner => 
            <Marker
              key={partner.id}
              position={{ lat: partner.address.latitude, lng: partner.address.longitude }}
              clusterer={clusterer}
              icon={
                selected?.id === partner.id ? activeMarkerIcon : partner.partnerId === partnerId ? partnerMarkerIcon :  defaultMarkerIcon
              }
              onClick={() => openInfoWindow(partner)}
            />
          )}</>
        }
      </MarkerClusterer>

      {selected && (
        <InfoWindow
          position={{ lat: selected.address.latitude, lng: selected.address.longitude }}
          onCloseClick={() => closeInfoWindow()} >
          <div className='infowindow'>
            <button
              className='infowindow-close z-50'
              onClick={() => closeInfoWindow()}>
              ✕
            </button>
            <PartnerCard partner={getSelectedPartnerForCard()} langCode={langCode} embedded={true} />
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  )
}